import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

import { validatePassword, minLengthFor } from '../utils/passwordPolicy';
import './PasswordField.css';

/**
 * Campo de contraseña con verla en claro y confirmación.
 *
 * El botón del ojo alterna el tipo del input, así que muestra exactamente lo
 * que se va a enviar: sirve para cachar espacios al final o un carácter de más,
 * que es de donde salían las contraseñas que "no coincidían" sin razón visible.
 *
 * La lista de requisitos se pinta en vivo para no descubrir hasta el submit que
 * la contraseña no cumple la política.
 *
 * Props:
 *   value / onChange        contraseña
 *   confirm / onConfirmChange   confirmación (si se omite, no se pide)
 *   accountType             'user' | 'admin' — define el mínimo de caracteres
 *   optional                true en edición: vacío significa "no cambiar"
 */
const PasswordField = ({
  value = '',
  onChange,
  confirm = '',
  onConfirmChange,
  accountType = 'user',
  label = 'Contraseña',
  optional = false,
  placeholder,
  autoComplete = 'new-password'
}) => {
  const [visible, setVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const pideConfirmacion = typeof onConfirmChange === 'function';
  const minLength = minLengthFor(accountType);
  const pol = validatePassword(value, accountType);

  // En edición, vacío es válido: quiere decir que no se cambia la contraseña.
  const vacio = !value;
  const enBlancoPermitido = optional && vacio;

  const coincide = !pideConfirmacion || enBlancoPermitido || (value === confirm && value.length > 0);
  const confirmTocada = confirm.length > 0;

  const requisitos = [
    { ok: pol.meetsLength, texto: `Al menos ${minLength} caracteres` },
    { ok: pol.hasUpperCase, texto: 'Una mayúscula' },
    { ok: pol.hasLowerCase, texto: 'Una minúscula' },
    { ok: pol.hasNumber, texto: 'Un número' },
    { ok: pol.hasSpecialChar, texto: 'Un carácter especial' }
  ];

  const inputClass = (hayError) => `admin-form-input pwdfield__input${hayError ? ' admin-form-input--error' : ''}`;

  return (
    <div className="pwdfield">
      <div className="admin-form-group">
        <label className="admin-form-label"><FontAwesomeIcon icon={faLock} /> {label}</label>
        <div className="pwdfield__wrap">
          <input
            type={visible ? 'text' : 'password'}
            className={inputClass(!enBlancoPermitido && !vacio && !pol.isValid)}
            value={value}
            autoComplete={autoComplete}
            placeholder={placeholder || (optional ? 'Dejar vacío para mantener la actual' : `Mínimo ${minLength} caracteres`)}
            onChange={(e) => onChange && onChange(e.target.value)}
          />
          <button
            type="button"
            className="pwdfield__toggle"
            onClick={() => setVisible(v => !v)}
            title={visible ? 'Ocultar contraseña' : 'Ver contraseña'}
            aria-label={visible ? 'Ocultar contraseña' : 'Ver contraseña'}
            aria-pressed={visible}
            tabIndex={-1}
          >
            <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
          </button>
        </div>

        {!enBlancoPermitido && !vacio && (
          <ul className="pwdfield__reqs">
            {requisitos.map((r) => (
              <li key={r.texto} className={r.ok ? 'pwdfield__req pwdfield__req--ok' : 'pwdfield__req'}>
                <FontAwesomeIcon icon={r.ok ? faCheck : faXmark} /> {r.texto}
              </li>
            ))}
          </ul>
        )}
        {!enBlancoPermitido && !vacio && !pol.meetsTypes && (
          <span className="admin-form-hint pwdfield__hint">
            Faltan tipos de carácter: se necesitan 3 de los 4 (llevas {pol.typesCount}).
          </span>
        )}
        {enBlancoPermitido && (
          <span className="admin-form-hint">Vacío = no cambiar la contraseña.</span>
        )}
      </div>

      {pideConfirmacion && (
        <div className="admin-form-group">
          <label className="admin-form-label"><FontAwesomeIcon icon={faLock} /> Confirmar {label.toLowerCase()}</label>
          <div className="pwdfield__wrap">
            <input
              type={confirmVisible ? 'text' : 'password'}
              className={inputClass(confirmTocada && !coincide)}
              value={confirm}
              autoComplete={autoComplete}
              placeholder={optional ? 'Repite solo si vas a cambiarla' : 'Repite la contraseña'}
              onChange={(e) => onConfirmChange(e.target.value)}
            />
            <button
              type="button"
              className="pwdfield__toggle"
              onClick={() => setConfirmVisible(v => !v)}
              title={confirmVisible ? 'Ocultar contraseña' : 'Ver contraseña'}
              aria-label={confirmVisible ? 'Ocultar contraseña' : 'Ver contraseña'}
              aria-pressed={confirmVisible}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={confirmVisible ? faEyeSlash : faEye} />
            </button>
          </div>
          {confirmTocada && (
            <span className={`admin-form-hint ${coincide ? 'pwdfield__match' : 'pwdfield__nomatch'}`}>
              <FontAwesomeIcon icon={coincide ? faCheck : faXmark} /> {coincide ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Misma regla que usa el componente para pintar el estado, expuesta para que la
 * validación del submit no la reimplemente y las dos puedan discrepar.
 */
export const passwordFieldsOk = (password, confirm, accountType, { optional = false } = {}) => {
  if (optional && !password) return { ok: true, motivo: null };
  if (!password) return { ok: false, motivo: 'Falta la contraseña' };
  if (!validatePassword(password, accountType).isValid) {
    return { ok: false, motivo: 'La contraseña no cumple la política' };
  }
  if (confirm != null && password !== confirm) {
    return { ok: false, motivo: 'Las contraseñas no coinciden' };
  }
  return { ok: true, motivo: null };
};

export default PasswordField;
